/**
 * Bedarfsplaner (Monatsansicht) — Phase 9 von #763 (siehe reference/bedarfsplaner/BEDARFSPLANER.md
 * und reference/crispy-wiggling-balloon.md). Teilt sich Appbar/Toolbar-Rahmen mit den anderen beiden
 * Ansichten, hat aber eigene Toolbar-Controls (Monatsnavigation, Bereich/Posten-Level-Toggle) statt der
 * Wochenplan-Wochennavigation, und kein Filter-Panel-Sidebar/Vorlagen-Sidebar (volle Breite fürs
 * Monatsgrid) — siehe viewSwitcher.js für das Ein-/Ausblenden dieser Controls.
 *
 * Das "+" in jeder Tageszelle legt einen Bedarf für den Tag an (Bedarf-Dialog im Anlegen-Modus).
 * Klick auf einen .pchip öffnet die Bedarf-Zuordnung; Klick auf einen .dchip klappt die Posten-Liste
 * dieser Zelle einzeln ein/aus (.bgrp.collapsed). Der Bereich/Posten-Umschalter klappt global alle
 * Bereiche zu/auf und setzt den Default — beides ohne Re-Render, siehe wechsleLevel/bedarfChip.js.
 */

import dayjs from '../../../../shared/services/dayjs.js';
import { stammdatenService } from '../services/StammdatenService.js';
import { bedarfService } from '../services/BedarfService.js';
import { groupiereBedarf } from '../util/bedarfState.js';
import { eventService } from '../services/EventService.js';
import { erstelleBereichsGruppe, erstelleEventGruppe } from '../components/bedarfChip.js';
import { fuelleWetterBadges } from '../components/weatherBadge.js';
import { rendereEventOverlay, berechneLanes, eventBlock, setzeAlleEventsEingeklappt } from '../components/monthEventOverlay.js';
import { oeffneBedarfZuordnung, oeffneNeuenBedarf } from '../dialogs/bedarfZuordnung.js';
import { oeffneEventEditor } from '../dialogs/eventEditor.js';
import { oeffneTagesvorlagen } from '../dialogs/tagesvorlagen.js';
import { oeffneWochenVorlagen, oeffneTagVorlagen, oeffneVorwocheUebernehmen } from '../components/vorlagenEinsetzen.js';
import {
	istBereichSichtbar,
	istPostenSichtbar,
	istEinsatzortSichtbar,
	istQualifikationSichtbar,
	renderFilterPanel,
} from '../components/filterPanel.js';
import { showGridLoader, hideGridLoader } from '../dienstplan.js';
import { convertNumberToEuroGanz, convertNumberToEuroKurz } from '../util.js';
import { Roles } from '../../../../../domain/konstanten/Roles.js';

const WOCHENTAGE = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const LEVEL_STORAGE_KEY = 'dienstplan.bedarfLevel';
const MODUS_STORAGE_KEY = 'dienstplan.bedarfModus';

const bedarfgrid = document.getElementById('bedarfgrid');
const bedarfgridContent = document.getElementById('bedarfgrid-content');
const monatLabel = document.getElementById('bedarf-monat-label');
const levelseg = document.getElementById('levelseg');
const bedarfFilterbar = document.getElementById('filterbar');
const modusseg = document.getElementById('bedarf-modusseg');
const kostenSumme = document.getElementById('bedarf-kosten-summe');

let monatAnker = dayjs.tz(new Date(), dayjs.tz.DEFAULT_TZ).startOf('month');
// level ist nur der DEFAULT-Klappzustand: 'bereich' = alle Bereiche zugeklappt, 'posten' = alle offen.
let level = localStorage.getItem(LEVEL_STORAGE_KEY) === 'bereich' ? 'bereich' : 'posten';
// Anzeige-Modus (KOSTENANZEIGE.md §3): 'besetzung' (filled/need) oder 'kosten' (geplante AG-Kosten). Der
// Kosten-Modus greift nur für Rollen mit Budget-Einsicht — nur die bekommen `geplant` vom Server.
let modus = localStorage.getItem(MODUS_STORAGE_KEY) === 'kosten' ? 'kosten' : 'besetzung';
const darfKostenSehen = () => [Roles.Sachbearbeiter, Roles.DienstplanerMitBudget].some((r) => window.myVars?.UserRoles?.includes(r));
const istKostenModus = () => modus === 'kosten' && darfKostenSehen();
// Zellen (`${tag}:${bereichID}`), deren Klappzustand vom level-Default ABWEICHT (individuell auf-/
// zugeklappt). Ein globaler Toggle setzt den Default und leert die Abweichungen.
const bgrpAbweichungen = new Set();
// Events (Chevron im Balken/Kartenkopf), deren Klappzustand vom level-Default ABWEICHT — wie bei den
// Bereichsgruppen: 'bereich' klappt auch alle Events zu, 'posten' alle auf. Bewusst nur im Speicher: übersteht
// das Neu-Rendern nach Zuordnen/Speichern, nicht aber ein Neuladen der Seite.
const eventAbweichungen = new Set();
// Einzeln zugeklappte Tagesgruppen mehrtägiger Events (`${eventID}:${YYYY-MM-DD}`), ebenfalls nur im Speicher.
const eingeklappteEventTage = new Set();

/** Ist die Bereichsgruppe dieser Zelle aktuell zugeklappt? level-Default XOR individuelle Abweichung. */
function istEingeklappt(collapseKey) {
	const defaultZu = level === 'bereich';
	return bgrpAbweichungen.has(collapseKey) ? !defaultZu : defaultZu;
}

/** Ist das Event aktuell zugeklappt? level-Default XOR individuelle Abweichung. */
function istEventEingeklappt(eventID) {
	const defaultZu = level === 'bereich';
	return eventAbweichungen.has(eventID) ? !defaultZu : defaultZu;
}

/** Klappzustand eines Events merken (als Abweichung vom level-Default). */
function merkeEventKlappzustand(eventID, eingeklappt) {
	if (eingeklappt !== (level === 'bereich')) eventAbweichungen.add(eventID); else eventAbweichungen.delete(eventID);
}

/**
 * @returns {array<dayjs>} alle Tage des Anzeige-Grids (volle Wochen Mo-So, die den Monat umschließen)
 */
function berechneGridTage() {
	const start = monatAnker.startOf('month').startOf('isoWeek');
	const ende = monatAnker.endOf('month').endOf('isoWeek').format('YYYY-MM-DD');
	const tage = [];
	// Jeden Tag aus seinem Datum neu in Europe/Berlin erzeugen: add() auf tz-Objekten behält den UTC-Offset des
	// Ausgangstags, nach der Zeitumstellung lägen die Tage sonst eine Stunde daneben.
	for (let i = 0; ; i += 1) {
		const tagString = start.add(i, 'day').format('YYYY-MM-DD');
		if (tagString > ende) break;
		tage.push(dayjs.tz(tagString, dayjs.tz.DEFAULT_TZ));
	}
	return tage;
}

function aktualisiereLevelSegUI() {
	if (!levelseg) return;
	levelseg.querySelectorAll('.o').forEach((el) => {
		el.classList.toggle('on', el.dataset.lv === level);
	});
}

/**
 * Traffic-Light-Legende + Bereich/Posten-"alle"-Chips + Warnhinweis-Caption, siehe BEDARFSPLANER.md
 * §1 (Filterbar-Zeile). Schreibt bewusst direkt in #filterbar statt über filterPanel.js's
 * renderFilterChips (das für die Wochenplan-/Mitarbeiteransicht andere Chip-Inhalte zeigt) — wird nach
 * renderFilterPanel() aufgerufen und überschreibt deren Chip-Inhalt absichtlich mit der
 * Bedarfsplaner-eigenen Zusammenfassung.
 * @param {array<object>} sichtbareZeilen bereits gefilterte, flache Bedarfszeilen des angezeigten Monats
 * @param {number} bereicheGesamt Anzahl aktiver Bereiche mit Posten
 * @param {number} postenGesamt Anzahl aktiver Posten
 */
function zeigeBedarfLegende(sichtbareZeilen, bereicheGesamt, postenGesamt) {
	if (!bedarfFilterbar) return;
	bedarfFilterbar.hidden = false;
	bedarfFilterbar.innerHTML = '';

	// Ampel-Legende (erfüllt/teilweise/offen) mit 9px bi-circle-fill Punkten, siehe BEDARFSPLANER.md §1.
	[['erfüllt', 'var(--pos)'], ['teilweise', 'var(--amber)'], ['offen', 'var(--neg)']].forEach(([label, farbe]) => {
		const eintrag = document.createElement('span');
		eintrag.className = 'fsum';
		eintrag.innerHTML = `<i class="bi bi-circle-fill" style="color:${farbe};font-size:9px;"></i>${label}`;
		bedarfFilterbar.appendChild(eintrag);
	});

	const sp = document.createElement('span');
	sp.className = 'sp';
	bedarfFilterbar.appendChild(sp);

	const bereicheSichtbar = new Set(sichtbareZeilen.map((z) => z.BereichID)).size;
	const postenSichtbar = new Set(sichtbareZeilen.map((z) => z.PostenID)).size;
	const bereichChip = document.createElement('span');
	bereichChip.className = 'fchip';
	bereichChip.innerHTML = `<span class="k">Bereich</span> ${bereicheSichtbar >= bereicheGesamt ? 'alle' : `${bereicheSichtbar} von ${bereicheGesamt}`}`;
	bedarfFilterbar.appendChild(bereichChip);
	const postenChip = document.createElement('span');
	postenChip.className = 'fchip';
	postenChip.innerHTML = `<span class="k">Posten</span> ${postenSichtbar >= postenGesamt ? 'alle' : `${postenSichtbar} von ${postenGesamt}`}`;
	bedarfFilterbar.appendChild(postenChip);

	const offeneBedarfe = sichtbareZeilen.filter((z) => z.filled < z.BedarfAnzahl).length;
	const offenePosten = sichtbareZeilen.reduce((summe, z) => summe + Math.max(0, z.BedarfAnzahl - z.filled), 0);
	if (offeneBedarfe > 0) {
		const warnung = document.createElement('span');
		warnung.className = 'fsum';
		warnung.innerHTML = `<i class="bi bi-exclamation-triangle"></i>${offeneBedarfe} offene Bedarfe · ${offenePosten} offene Posten diesen Monat`;
		bedarfFilterbar.appendChild(warnung);
	}
}

// Stand des letzten vollen Renders — Grundlage für gezielte Aktualisierungen einzelner Tage (aktualisiereTage),
// damit Bedarf-Dialog und Tagesvorlagen nicht den ganzen Monat neu aufbauen müssen.
let ansicht = null; // { bedarfZeilen, zellenNachTag, kwNachMontag, oeffneBedarf, bereicheGesamt, postenGesamt }

const tagVon = (zeile) => dayjs(zeile.Datum).format('YYYY-MM-DD');

/** Passt die Bedarfszeile zu den Filtern des Filter-Panels? */
function istZeileSichtbar(z) {
	return istBereichSichtbar(z.BereichID)
		&& istPostenSichtbar(z.PostenID)
		&& istQualifikationSichtbar(z.QualifikationID ? [z.QualifikationID] : [])
		&& istEinsatzortSichtbar(z.EinsatzortID ? [z.EinsatzortID] : []);
}

/** Anzahl vorhandener Bedarfszeilen (ungefiltert) ab einem Tag — für den Hinweis in den Vorlagen-Popovern. */
function bedarfAnzahl(vonTag, anzahlTage) {
	const tagKeys = new Set(Array.from({ length: anzahlTage }, (_, i) => vonTag.add(i, 'day').format('YYYY-MM-DD')));
	return (ansicht?.bedarfZeilen ?? []).filter((z) => tagKeys.has(tagVon(z))).length;
}

/**
 * Baut die Bereichsgruppen (.mcell-bedarf) einer Tageszelle (neu) auf. Event-Bedarf steht nicht hier, sondern im
 * Event-Block/Overlay. Klicks auf einen Chip aktualisieren nach Änderungen nur diesen Tag.
 * @param {HTMLElement} zelle .mcell
 * @param {dayjs} tag
 * @param {Map|undefined} bereiche Bereichsgruppen des Tages aus groupiereBedarf
 */
function fuelleBedarfSpalte(zelle, tag, bereiche) {
	zelle.querySelector('.mcell-bedarf')?.remove();
	if (!bereiche) return;
	const tagKey = tag.format('YYYY-MM-DD');
	const spalte = document.createElement('div');
	spalte.className = 'mcell-bedarf';
	Array.from(bereiche.values())
		.sort((a, b) => a.bereich.localeCompare(b.bereich))
		.forEach((bereichsGruppe) => {
			const collapseKey = `${tagKey}:${bereichsGruppe.bereichID}`;
			const bgrp = erstelleBereichsGruppe(bereichsGruppe, {
				eingeklappt: istEingeklappt(collapseKey),
				onPostenKlick: (zeile) => ansicht.oeffneBedarf(zeile.bedarfID, () => aktualisiereTage(tag, 1)),
				// Individuelles Ein-/Ausklappen (Klassen-Toggle in bedarfChip, kein Re-Render). Wir merken
				// die ABWEICHUNG vom level-Default, damit sie ein echtes Neu-Rendern übersteht.
				onToggleCollapse: (_gruppe, jetztEingeklappt) => {
					if (jetztEingeklappt !== (level === 'bereich')) {
						bgrpAbweichungen.add(collapseKey);
					} else {
						bgrpAbweichungen.delete(collapseKey);
					}
				},
			});
			spalte.appendChild(bgrp);
		});
	// vor einer evtl. vorhandenen Kosten-Fußzeile einfügen, die immer unten in der Zelle steht
	zelle.insertBefore(spalte, zelle.querySelector('.ko-foot'));
}

/** Sichtbare Bedarfszeilen (Filter-Panel) des letzten Renders, optional auf eine Menge von Tagen beschränkt. */
function sichtbareZeilenVon(tagKeys) {
	return (ansicht?.bedarfZeilen ?? []).filter((z) => istZeileSichtbar(z) && (!tagKeys || tagKeys.has(tagVon(z))));
}

/** Sichtbare Bedarfszeilen nur des angezeigten Monats (ohne die Nachbarmonatstage am Rand des Grids). */
function sichtbareMonatsZeilen() {
	const monatKey = monatAnker.format('YYYY-MM');
	return sichtbareZeilenVon().filter((z) => tagVon(z).startsWith(monatKey));
}

const offenePostenVon = (zeilen) => zeilen.reduce((summe, z) => summe + Math.max(0, z.BedarfAnzahl - z.filled), 0);
const geplantVon = (zeilen) => zeilen.reduce((summe, z) => summe + (z.geplant || 0), 0);

/**
 * Kosten-Fußzeile einer Tageszelle (KOSTENANZEIGE.md §4): links "n offen", rechts die Tagessumme der geplanten
 * AG-Kosten — inkl. Event-Bedarf, der sonst im Overlay steht. Die Summen (Tag, KW, Monat) stehen für Budget-Rollen
 * in beiden Modi; der Besetzung/Kosten-Umschalter wechselt nur die Werte in den Chips. Nur für Tage des Monats.
 * @param {HTMLElement} zelle .mcell
 * @param {string} tagKey YYYY-MM-DD
 */
function fuelleKostenFuss(zelle, tagKey) {
	zelle.querySelector('.ko-foot')?.remove();
	if (!darfKostenSehen() || zelle.classList.contains('out')) return;
	const zeilen = sichtbareZeilenVon(new Set([tagKey]));
	const offen = offenePostenVon(zeilen);
	const geplant = geplantVon(zeilen);
	const fuss = document.createElement('div');
	fuss.className = 'ko-foot tot';
	fuss.innerHTML = `<span>${offen > 0 ? `<span class="ko-open"><i class="bi bi-exclamation-circle"></i>${offen} offen</span>` : ''}</span>`
		+ `<span class="ko-v${geplant > 0 ? '' : ' z'}" title="Geplante AG-Kosten des Tages">${convertNumberToEuroGanz(geplant)}</span>`;
	zelle.appendChild(fuss);
}

/**
 * Wochensummen in der KW-Spalte (Kurzform, voller Betrag im Tooltip) sowie Monatssumme und offene Posten in der
 * Toolbar (KOSTENANZEIGE.md §3/§5). Rechnet aus den geladenen Zeilen, ohne Server-Anfrage.
 */
function aktualisiereKostenSummen() {
	ansicht?.kwNachMontag.forEach((kw, montagKey) => {
		kw.querySelector('.ko-ws')?.remove();
		if (!darfKostenSehen()) return;
		const montag = dayjs.tz(montagKey, dayjs.tz.DEFAULT_TZ);
		const wocheKeys = new Set(Array.from({ length: 7 }, (_, i) => montag.add(i, 'day').format('YYYY-MM-DD')));
		const geplant = geplantVon(sichtbareZeilenVon(wocheKeys));
		const ws = document.createElement('span');
		ws.className = 'ko-ws';
		ws.title = `Geplante AG-Kosten KW ${montag.add(14, 'hours').isoWeek()}: ${convertNumberToEuroGanz(geplant)}`;
		ws.textContent = convertNumberToEuroKurz(geplant);
		kw.appendChild(ws);
	});
	if (!kostenSumme) return;
	if (!darfKostenSehen() || !ansicht) {
		kostenSumme.innerHTML = '';
		return;
	}
	const monatZeilen = sichtbareMonatsZeilen();
	const offen = offenePostenVon(monatZeilen);
	// Erklärung der Summen als Tooltip (statt einer Legende, die in der Toolbar zu viel Platz braucht)
	kostenSumme.title = 'Geplante AG-Kosten: nur zugeordnete Schichten · offene Posten ohne Kosten';
	kostenSumme.innerHTML = (offen > 0 ? `<span class="ko-open"><i class="bi bi-exclamation-circle"></i>${offen} offen</span><span class="dv"></span>` : '')
		+ `<span><b>${monatAnker.format('MMMM')} · ${convertNumberToEuroGanz(geplantVon(monatZeilen))}</b> geplant</span>`;
}

/**
 * Sichtbarkeit der Kosten-Controls in der Toolbar: Umschalter und Monatssumme nur in der Bedarf-Ansicht für
 * Budget-Rollen. viewSwitcher.js ruft das bei jedem Ansichtswechsel.
 * @param {boolean} bedarfAnsichtAktiv
 */
function aktualisiereKostenToolbar(bedarfAnsichtAktiv) {
	const zeigeUmschalter = bedarfAnsichtAktiv && darfKostenSehen();
	// Alle Zähler enthalten Besetzung UND Kosten (bedarfState.js:fuelleZaehler); .ko-modus schaltet per CSS um.
	bedarfgrid?.classList.toggle('ko-modus', istKostenModus());
	modusseg?.classList.toggle('view-hidden', !zeigeUmschalter);
	kostenSumme?.classList.toggle('view-hidden', !zeigeUmschalter);
	modusseg?.querySelectorAll('.o').forEach((el) => el.classList.toggle('on', el.dataset.modus === modus));
}

/** Besetzung ⇄ Kosten: reiner Klassen-Toggle (siehe aktualisiereKostenToolbar), kein Neu-Rendern. */
function wechsleModus(neuerModus) {
	if (neuerModus === modus) return;
	modus = neuerModus;
	localStorage.setItem(MODUS_STORAGE_KEY, modus);
	aktualisiereKostenToolbar(true);
}

/**
 * Lädt den Bedarf einzelner Tage nach und baut nur deren Bereichsgruppen sowie die Legende neu auf — ohne
 * Lade-Kreis, Scroll-Sprung oder Neuaufbau des Monats. Das Event-Overlay positioniert sich über seinen
 * ResizeObserver selbst nach. Nur für Änderungen an normalem (nicht Event-)Bedarf.
 * @param {dayjs} von erster Tag
 * @param {number} anzahlTage
 */
async function aktualisiereTage(von, anzahlTage) {
	if (!ansicht) {
		await zeigeBedarfsplaner();
		return;
	}
	const neu = await bedarfService.holeBedarfZeitraum(von, von.add(anzahlTage - 1, 'day'));
	const tagKeys = Array.from({ length: anzahlTage }, (_, i) => von.add(i, 'day').format('YYYY-MM-DD'));
	ansicht.bedarfZeilen = [...ansicht.bedarfZeilen.filter((z) => !tagKeys.includes(tagVon(z))), ...neu];
	const tageMap = groupiereBedarf(neu.filter((z) => istZeileSichtbar(z) && !z.EventID));
	tagKeys.forEach((tagKey, i) => {
		const zelle = ansicht.zellenNachTag.get(tagKey);
		if (zelle) {
			fuelleBedarfSpalte(zelle, von.add(i, 'day'), tageMap.get(tagKey));
			fuelleKostenFuss(zelle, tagKey);
		}
	});
	aktualisiereKostenSummen();
	zeigeBedarfLegende(sichtbareMonatsZeilen(), ansicht.bereicheGesamt, ansicht.postenGesamt);
}

/**
 * Befüllt den Bedarfsplaner (Monatsgrid) mit allen Daten und Controls.
 * @param {boolean} betriebWechsel Stammdaten-Caches (Posten/Einsatzorte/Mitarbeiter) neu laden?
 */
async function zeigeBedarfsplaner(betriebWechsel = false) {
	if (!bedarfgrid || !bedarfgridContent) return;
	showGridLoader(bedarfgrid);
	document.body.style.cursor = 'wait';

	const postenListe = await stammdatenService.getPosten(betriebWechsel);
	const einsatzorte = await stammdatenService.getEinsatzorte(betriebWechsel);
	const qualifikationen = await stammdatenService.getQualifikationen();
	const mitarbeiterliste = await stammdatenService.getMitarbeiter(betriebWechsel);
	const unternehmen = await stammdatenService.getUnternehmensdaten();
	const bereicheAktiv = unternehmen.Bereiche.filter((b) => b.Verwenden);
	const bereichePostenAktiv = bereicheAktiv.filter((b) => postenListe.some((p) => p.BereichID === b._id));

	renderFilterPanel(bereichePostenAktiv, postenListe, einsatzorte, qualifikationen, mitarbeiterliste, () => zeigeBedarfsplaner());

	if (monatLabel) {
		monatLabel.innerHTML = `<b>${monatAnker.format('MMMM YYYY')}</b>`;
	}

	const tage = berechneGridTage();
	const bedarfZeilen = await bedarfService.holeBedarfZeitraum(tage[0], tage[tage.length - 1]);
	const events = await eventService.holeEventsZeitraum(tage[0], tage[tage.length - 1]);
	const sichtbareZeilen = bedarfZeilen.filter(istZeileSichtbar);
	// Event-gebundene Bedarfe NICHT in die normalen Bereich-Chips mischen: eintägige Events zeigen ihre
	// Zeilen in der aufgeklappten Overlay-Karte, mehrtägige in einer eigenen Event-Gruppe je Zelle.
	const tageMap = groupiereBedarf(sichtbareZeilen.filter((z) => !z.EventID));

	const eventById = new Map(events.map((e) => [e._id, e]));
	const laneById = berechneLanes(events);
	const istEintaegigEvent = (ev) => dayjs(ev.DatumVon).isSame(dayjs(ev.DatumBis), 'day');
	const neuLaden = () => zeigeBedarfsplaner();
	// Bedarf-Dialog öffnen; bei Event-Bedarfen bietet der Dialog "Event öffnen" an. Event-Bedarf hängt am
	// Overlay (Karten/Deckungs-Badges) und lädt daher nach Änderungen den Monat neu, normaler Bedarf nur seinen Tag.
	const oeffneBedarf = (bedarfID, onChange = neuLaden) => oeffneBedarfZuordnung(bedarfID, onChange, {
		onEventOeffnen: (eventID) => {
			const ev = eventById.get(eventID);
			if (ev) oeffneEventEditor(ev, monatAnker, neuLaden);
		},
	});

	// Bedarfszeilen mehrtägiger Events je Tag/Event (für die In-Zelle-Gruppen). Eintägige Events werden
	// ausgelassen — die erscheinen nur in der Overlay-Karte.
	const mehrtagEventBedarf = new Map(); // YYYY-MM-DD -> Map(eventId -> rows[])
	sichtbareZeilen.forEach((z) => {
		const ev = z.EventID && eventById.get(z.EventID);
		if (!ev || istEintaegigEvent(ev)) return;
		const tag = dayjs(z.Datum).format('YYYY-MM-DD');
		if (!mehrtagEventBedarf.has(tag)) mehrtagEventBedarf.set(tag, new Map());
		const perEvent = mehrtagEventBedarf.get(tag);
		if (!perEvent.has(z.EventID)) perEvent.set(z.EventID, []);
		perEvent.get(z.EventID).push(z);
	});

	// Event-Bedarfszeilen (ungefiltert) je Event für das Overlay — die Deckungs-Badges/Karten sollen den
	// echten Gesamtbedarf des Events zeigen, unabhängig vom aktiven Bereich/Posten-Filter.
	const bedarfProEvent = new Map();
	bedarfZeilen.forEach((z) => {
		if (!z.EventID) return;
		if (!bedarfProEvent.has(z.EventID)) bedarfProEvent.set(z.EventID, []);
		bedarfProEvent.get(z.EventID).push(z);
	});
	// Zellen je Tag für die Positionierung des Overlays.
	const zellenNachTag = new Map();
	// KW-Zellen je Montag (YYYY-MM-DD) für die Wochensummen im Kosten-Modus.
	const kwNachMontag = new Map();
	ansicht = {
		bedarfZeilen,
		zellenNachTag,
		kwNachMontag,
		oeffneBedarf,
		bereicheGesamt: bereichePostenAktiv.length,
		postenGesamt: postenListe.length,
	};

	bedarfgridContent.innerHTML = '';
	bedarfgridContent.className = 'month';

	// Leere Kopfzelle der schmalen KW-Spalte (1. Spalte links; "KW" steht in jeder Wochenzeile), danach die Wochentage.
	const kwKopf = document.createElement('div');
	kwKopf.className = 'mkw-head';
	bedarfgridContent.appendChild(kwKopf);
	WOCHENTAGE.forEach((label, i) => {
		const kopf = document.createElement('div');
		kopf.className = `mhead${i >= 5 ? ' we' : ''}`;
		kopf.textContent = label;
		bedarfgridContent.appendChild(kopf);
	});

	const heute = dayjs.tz(new Date(), dayjs.tz.DEFAULT_TZ).startOf('day');
	tage.forEach((tag, i) => {
		// Zu Beginn jeder Wochenzeile (Mo) die KW-Nummer in die linke Spalte. Der Montag ist tage[i];
		// +14h zur zeitzonensicheren isoWeek-Berechnung wie im Wochenplan.
		if (i % 7 === 0) {
			const kw = document.createElement('div');
			kw.className = 'mkw';
			const label = document.createElement('span');
			label.className = 'k';
			label.textContent = 'KW';
			const nummer = document.createElement('span');
			nummer.className = 'n';
			nummer.textContent = tag.add(14, 'hours').isoWeek();
			// Vorlagen in die Woche dieser Zeile einsetzen (TAGESVORLAGEN.md §4.1).
			const vorlagenBtn = document.createElement('button');
			vorlagenBtn.type = 'button';
			vorlagenBtn.className = 'wt-ib';
			vorlagenBtn.title = 'Vorlagen einsetzen';
			vorlagenBtn.innerHTML = '<i class="bi bi-layers"></i>';
			vorlagenBtn.addEventListener('click', () => oeffneWochenVorlagen({
				anker: vorlagenBtn, montag: tag, bestehend: bedarfAnzahl(tag, 7), onFertig: () => aktualisiereTage(tag, 7),
			}));
			// Bedarf der Vorwoche in diese Woche übernehmen (TAGESVORLAGEN.md §4).
			const vorwocheBtn = document.createElement('button');
			vorwocheBtn.type = 'button';
			vorwocheBtn.className = 'wt-ib';
			vorwocheBtn.title = 'Vorwoche übernehmen';
			vorwocheBtn.innerHTML = '<i class="bi bi-copy"></i>';
			vorwocheBtn.addEventListener('click', () => oeffneVorwocheUebernehmen({
				anker: vorwocheBtn, montag: tag, bestehend: bedarfAnzahl(tag, 7), onFertig: () => aktualisiereTage(tag, 7),
			}));
			kw.append(label, nummer, vorwocheBtn, vorlagenBtn);
			kwNachMontag.set(tag.format('YYYY-MM-DD'), kw);
			bedarfgridContent.appendChild(kw);
		}
		const istAktuellerMonat = tag.isSame(monatAnker, 'month');
		const istWochenende = tag.isoWeekday() >= 6;
		const istHeute = tag.isSame(heute, 'day');
		const zelle = document.createElement('div');
		zelle.className = `mcell${istAktuellerMonat ? '' : ' out'}${istWochenende ? ' we' : ''}${istHeute ? ' today' : ''}`;

		const dn = document.createElement('div');
		dn.className = 'dn';
		const dnum = document.createElement('span');
		dnum.className = 'dnum';
		dnum.textContent = istAktuellerMonat ? tag.format('D') : tag.format('D.MM');
		dn.appendChild(dnum);
		// Tagesvorlage(n) auf diesen Tag einsetzen (nur bei Hover sichtbar, TAGESVORLAGEN.md §5).
		const vorlageTag = document.createElement('button');
		vorlageTag.type = 'button';
		vorlageTag.className = 'dib';
		vorlageTag.title = `Tagesvorlage am ${tag.format('DD.MM.')} einsetzen`;
		vorlageTag.innerHTML = '<i class="bi bi-layers"></i>';
		vorlageTag.addEventListener('click', () => oeffneTagVorlagen({
			anker: vorlageTag, tag, bestehend: bedarfAnzahl(tag, 1), onFertig: () => aktualisiereTage(tag, 1),
		}));
		dn.appendChild(vorlageTag);
		// Neues Event an diesem Tag anlegen (gleicher Stil wie das Tagesvorlagen-Werkzeug, nur bei Hover sichtbar).
		const eventTag = document.createElement('button');
		eventTag.type = 'button';
		eventTag.className = 'dib';
		eventTag.title = `Event am ${tag.format('DD.MM.')} anlegen`;
		eventTag.innerHTML = '<i class="bi bi-stars"></i>';
		eventTag.addEventListener('click', () => oeffneEventEditor(null, monatAnker, () => zeigeBedarfsplaner(), tag));
		dn.appendChild(eventTag);
		// Bedarf für diesen Tag anlegen (nur bei Hover sichtbar, siehe .addbedarf im CSS).
		const addBedarf = document.createElement('span');
		addBedarf.className = 'addbedarf';
		addBedarf.title = `Bedarf am ${tag.format('DD.MM.')} anlegen`;
		addBedarf.innerHTML = '<i class="bi bi-plus-lg"></i>';
		addBedarf.addEventListener('click', () => oeffneNeuenBedarf(tag.format('YYYY-MM-DD'), () => aktualisiereTage(tag, 1)));
		dn.appendChild(addBedarf);
		// Wetter-Platzhalter rechts neben der Tageszahl (nur im aktuellen Monat) — wird von
		// fuelleWetterBadges() nach dem Grid-Aufbau befüllt (dokumentweite [data-wetter-tag]-Abfrage,
		// fire-and-forget, siehe components/weatherBadge.js).
		if (istAktuellerMonat) {
			const wetterBadge = document.createElement('span');
			wetterBadge.className = 'wx';
			wetterBadge.setAttribute('data-wetter-tag', tag.format('YYYY-MM-DD'));
			dn.appendChild(wetterBadge);
		}
		zelle.appendChild(dn);

		const tagKey = tag.format('YYYY-MM-DD');
		zellenNachTag.set(tagKey, zelle);
		const eventGruppen = mehrtagEventBedarf.get(tagKey);
		// Mehrtägige Events: je Event eine farbig umrandete Gruppe mit den Bedarfen des Tages — im Event-Block
		// direkt unter den Balken und in deren Lane-Reihenfolge, damit jede Gruppe unter ihrem Balken steht.
		if (eventGruppen) {
			const block = eventBlock(zelle);
			Array.from(eventGruppen.entries())
				.sort(([a], [b]) => (laneById.get(a) ?? 0) - (laneById.get(b) ?? 0))
				.forEach(([eventId, rows]) => {
					const ev = eventById.get(eventId);
					if (!ev) return;
					const tagSchluessel = `${eventId}:${tagKey}`;
					block.appendChild(erstelleEventGruppe(ev, rows, {
						eingeklappt: istEventEingeklappt(eventId),
						tagEingeklappt: eingeklappteEventTage.has(tagSchluessel),
						onPostenKlick: (bedarfID) => oeffneBedarf(bedarfID),
						onEventKlick: (event) => oeffneEventEditor(event, monatAnker, neuLaden),
						onTagToggle: (eingeklappt) => {
							if (eingeklappt) eingeklappteEventTage.add(tagSchluessel); else eingeklappteEventTage.delete(tagSchluessel);
						},
					}));
				});
		}
		fuelleBedarfSpalte(zelle, tag, tageMap.get(tagKey));
		fuelleKostenFuss(zelle, tagKey);
		bedarfgridContent.appendChild(zelle);
	});

	// Event-Overlay über das Grid legen (absolute Balken/Karten, Spacer in den Zellen). Nach dem
	// Aufbau aller Zellen, damit die Positionierung die echten Zell-Offsets messen kann.
	// Overlay: mehrtägige Events als spannenden Balken oben, eintägige als aufgeklappte Karte. Die
	// per-Tag-Bedarfe mehrtägiger Events zeigen zusätzlich die In-Zelle-Gruppen (ohne eigenen Titel,
	// da der Balken das Event bereits beschriftet).
	rendereEventOverlay({
		month: bedarfgridContent,
		events,
		bedarfProEvent,
		tageStrings: tage.map((t) => t.format('YYYY-MM-DD')),
		zellenNachTag,
		onEventKlick: (ev) => oeffneEventEditor(ev, monatAnker, () => zeigeBedarfsplaner()),
		onEvrowKlick: (bedarfID) => oeffneBedarf(bedarfID),
		istEingeklappt: istEventEingeklappt,
		onKlappzustand: merkeEventKlappzustand,
	});

	// Zusammenfassung nur über den angezeigten Monat, passend zu "diesen Monat" und zur Monatssumme in der Toolbar
	zeigeBedarfLegende(sichtbareMonatsZeilen(), bereichePostenAktiv.length, postenListe.length);
	aktualisiereLevelSegUI();
	aktualisiereKostenSummen();
	aktualisiereKostenToolbar(true);
	// Wetter-Icons nachladen — bewusst nicht awaited, siehe components/weatherBadge.js.
	fuelleWetterBadges();

	hideGridLoader(bedarfgrid);
	document.body.style.cursor = 'default';
}

function wechsleMonat(anzahl) {
	// Neu aus dem Datum erzeugen statt nur add(): sonst behält der Anker über die Zeitumstellung hinweg den alten
	// UTC-Offset, liegt real im Vormonat und alle Tage des neuen Monats erscheinen als "Nachbarmonat" (grau).
	monatAnker = dayjs.tz(monatAnker.add(anzahl, 'month').format('YYYY-MM-01'), dayjs.tz.DEFAULT_TZ);
	zeigeBedarfsplaner();
}

function heuteBedarf() {
	monatAnker = dayjs.tz(new Date(), dayjs.tz.DEFAULT_TZ).startOf('month');
	zeigeBedarfsplaner();
}

/**
 * Globaler Klapp-Umschalter: 'bereich' klappt alle Bereiche und Events zu, 'posten' alle auf. Setzt den neuen
 * Default, verwirft die individuellen Abweichungen und togglet `.collapsed` direkt auf allen Bereichs-
 * gruppen im DOM (kein Re-Render). Danach lässt sich weiterhin jede Gruppe einzeln auf-/zuklappen.
 */
function wechsleLevel(neuesLevel) {
	if (neuesLevel === level) return;
	level = neuesLevel;
	localStorage.setItem(LEVEL_STORAGE_KEY, level);
	bgrpAbweichungen.clear();
	const alleZu = level === 'bereich';
	bedarfgridContent?.querySelectorAll('.bgrp').forEach((b) => b.classList.toggle('collapsed', alleZu));
	// Events folgen demselben Default: 'bereich' klappt alle zu, 'posten' alle auf (Tagesgruppen behalten ihren Zustand).
	eventAbweichungen.clear();
	setzeAlleEventsEingeklappt(alleZu);
	aktualisiereLevelSegUI();
}

/**
 * Verdrahtet die Monatsnavigation und den Bereich/Posten-Toggle (einmalig, die Toolbar-Elemente selbst
 * sind statisches HTML). Wird von viewSwitcher.js beim ersten Wechsel auf die Bedarf-Ansicht gerufen.
 */
function initBedarfsplanerToolbar() {
	document.getElementById('bedarf-monat-prev')?.addEventListener('click', () => wechsleMonat(-1));
	document.getElementById('bedarf-monat-next')?.addEventListener('click', () => wechsleMonat(1));
	document.getElementById('bedarf-heute-btn')?.addEventListener('click', () => heuteBedarf());
	document.getElementById('bedarf-event-btn')?.addEventListener('click', () => oeffneEventEditor(null, monatAnker, () => zeigeBedarfsplaner()));
	document.getElementById('bedarf-tagesvorlagen-btn')?.addEventListener('click', () => oeffneTagesvorlagen());
	levelseg?.querySelectorAll('.o').forEach((el) => {
		el.addEventListener('click', () => wechsleLevel(el.dataset.lv));
	});
	modusseg?.querySelectorAll('.o').forEach((el) => {
		el.addEventListener('click', () => wechsleModus(el.dataset.modus));
	});
	aktualisiereLevelSegUI();
}

export { zeigeBedarfsplaner, initBedarfsplanerToolbar, aktualisiereKostenToolbar };
